export interface DebouncedCallback<Args extends unknown[]> {
    (...args: Args): void;
    cancel: () => void;
}

// For event-driven reloads (e.g. an SSE handler that reloads on every plugin_event), not reactive
// signals - useDebounced (hooks/useDebounced.ts) covers the signal case. An actively selling bot can
// fire many sellbot plugin_events per second; without coalescing, each one reruns a full reload and
// reconcile of the offer/stock stores, which is what made the identity wizard's item grid (rendered
// on top of that churn) stay janky for as long as the dialog was open.
// maxWaitMs caps how long a steady stream of calls can postpone the run, or a busy source never refreshes.
export function debounce<Args extends unknown[]>(fn: (...args: Args) => void, delayMs: number, maxWaitMs?: number): DebouncedCallback<Args> {
    let handle: ReturnType<typeof setTimeout> | undefined;
    let firstCallAt: number | undefined;
    const debounced = (...args: Args) => {
        const now = Date.now();
        firstCallAt ??= now;
        if (handle !== undefined) clearTimeout(handle);
        const waitMs = maxWaitMs === undefined ? delayMs : Math.max(0, Math.min(delayMs, firstCallAt + maxWaitMs - now));
        handle = setTimeout(() => { handle = undefined; firstCallAt = undefined; fn(...args); }, waitMs);
    };
    debounced.cancel = () => {
        if (handle !== undefined) { clearTimeout(handle); handle = undefined; }
        firstCallAt = undefined;
    };
    return debounced;
}
